import React, { useState, useEffect } from 'react';
import { Cart, ShippingMethod } from 'shared/types/cart';
import { Money } from 'shared/types/product';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import Loader from '@/components/customercenter-ui/common/loader/loader';
import {
  isExpShippingAvailable,
  isPriorityShippingAvailable,
} from '@/components/customercenter-ui/components/utils/cartUtils';
import { formatArrivalDate } from '@/components/customercenter-ui/components/utils/formatUtils';
import {
  createFedexRequestPayload,
  getArrivalDateForShippingMethod,
  getShippingMethodDisplayName,
} from '@/components/customercenter-ui/utils/fedexUtils';
import { useCheckout } from '@/context/checkoutProvider';
import { RadioGroup, RadioGroupItem } from '@/customercenter-ui/ui/radio-group';
import { useCart, useProduct } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { CurrencyHelpers } from '@/helpers/utils/currencyHelpers';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { getSelectedSite } from '@/helpers/utils/siteUtils';
import { GetEstimatedDeliveryDatePayload } from '@/sdk/composable-commerce/types/payloads/GetEstimatedDeliveryDatePayload';
import { ORDER_TYPES } from '../../../../../constants/constant';

export interface ShippingMethodRadioProps {
  shippingMethods: ShippingMethod[];
  cartData: Cart;
}
//shipmethod
const ShippingMethodRadio: React.FC<ShippingMethodRadioProps> = ({ cartData, shippingMethods }) => {
  const { setShippingMethod } = useCart(false);
  const [shippingMethodsList, setShippingMethodsList] = useState(shippingMethods);
  const isNoChargeOrder =
    cartData?.additionalDetails?.orderType === ORDER_TYPES.NO_CHARGE ||
    (cartData?.additionalDetails?.orderType === ORDER_TYPES.MAIL_ORDER &&
      cartData?.lineItems?.every((item) => item.productKey === '999999'));

  const {
    setShippingMethodValue,
    shippingMethod,
    setError,
    setNotificationMsg,
    setShowShippingNotApplicableError,
    showShippingNotApplicableError,
    setSelectedShippingMethod,
    selectedShippingMethod,
    setCartDeliveryDate,
    lineItemsAvailibiltyMsgs,
    shippingBackorderDate,
    shippingAddress,
    setContextCart,
  } = useCheckout();

  const [selectedShippingMethodId, setSelectedShippingMethodId] = useState(null as any);
  const [isDataAvailable, setIsDataAvailable] = useState(false);
  const { getEstimatedDeliveryDate } = useProduct();
  const [fedexResponse, setFedexResponse] = useState();
  const projectSettings: ProjectSettings = useProjSettings();
  const [isLoading, setIsLoading] = useState(false);

  const [fedexCallFlag, setFedexCallFlag] = useState(false);
  const lineItems = cartData?.lineItems;
  const shippingZipCode = shippingAddress?.postalCode;

  const isFreeShippingEligible = (shippingMethodName?: string): boolean => {
    const freeShippingEnabled = projectSettings?.freeShippingEnabled;
    const freeShipingMthdNames = projectSettings?.freeShipingMthdNames;
    const selectedSite = getSelectedSite() ?? 'ZBSite';

    if (!freeShippingEnabled || !freeShipingMthdNames || !shippingMethodName) {
      return false;
    }

    try {
      const freeShippingEnableSites = JSON.parse(freeShippingEnabled) as Record<string, boolean>;
      const isSiteEnabled = freeShippingEnableSites?.[selectedSite] === true;
      const methodNames = freeShipingMthdNames.split(',').map((name) => name.trim());

      const shippingMethodValue = selectedSite + shippingMethodName;
      const isMethodEligible = methodNames?.includes(shippingMethodValue);

      return isSiteEnabled && isMethodEligible;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (shippingMethods) {
      setShippingMethodsList(shippingMethods);
    }
  }, [shippingMethods]);

  useEffect(() => {
    if (shippingMethod) {
      setIsDataAvailable(true);
      setSelectedShippingMethodId(shippingMethod);
      setShippingMethodValue(shippingMethod);
    } else if (!isDataAvailable && shippingMethodsList && shippingMethodsList?.length > 0) {
      setIsDataAvailable(true);
      const standardMethodId =
        shippingMethodsList?.find(
          (method: any) => method?.displayName?.toLowerCase() === mapLocaleToMeaningfulFormat().standardShippingMethod,
        ) ?? shippingMethodsList[0];
      setSelectedShippingMethodId(standardMethodId?.shippingMethodId);
      setShippingMethodValue(standardMethodId?.shippingMethodId);
    }
  }, [shippingMethodsList]);

  useEffect(() => {
    const currentSelectedShippingMethod = shippingMethodsList?.find((method) => {
      return method?.shippingMethodId === selectedShippingMethodId;
    });

    if (currentSelectedShippingMethod) {
      setSelectedShippingMethod(currentSelectedShippingMethod);
      if (!(currentSelectedShippingMethod?.isApplicable === false)) {
        setShowShippingNotApplicableError(false);
      } else {
        setShowShippingNotApplicableError(true);
      }
    }
  }, [selectedShippingMethodId]);

  const handleShippingMethodChange = async (shippingInfo: any) => {
    setIsLoading(true);
    const { shippingMethodId } = shippingInfo;
    setShowShippingNotApplicableError(false);
    await setShippingMethod(shippingMethodId)
      .then((updatedCart) => {
        if (updatedCart) {
          setContextCart(updatedCart);
        }
        setSelectedShippingMethodId(shippingMethodId);
        setShippingMethodValue(shippingMethodId);
        localStorage.setItem('cartItemsDate', JSON.stringify(shippingMethodId));
        setCartDeliveryDate(JSON.stringify(shippingInfo));
      })
      .catch(() => {
        setError(true);
        setNotificationMsg(mapLocaleToMeaningfulFormat().shippingMethodError);
      });
    setIsLoading(false);
  };

  const filterShippingMethods = (
    isExpShippingProductAvailable: boolean,
    isPriorityShippingProductAvailable: boolean,
  ) => {
    if (shippingMethodsList && shippingMethodsList?.length > 0) {
      let updatedShippingMethods: any;
      updatedShippingMethods = shippingMethodsList?.filter((shippingMethod: any) => shippingMethod?.isApplicable);
      if (!isExpShippingProductAvailable || !isPriorityShippingProductAvailable) {
        if (!isExpShippingProductAvailable) {
          updatedShippingMethods = updatedShippingMethods?.filter((shippingMethod: any) => {
            return shippingMethod?.displayName?.toLowerCase() !== 'express';
          });
        }
        if (!isPriorityShippingProductAvailable) {
          updatedShippingMethods = updatedShippingMethods?.filter((shippingMethod: any) => {
            return shippingMethod?.displayName?.toLowerCase() !== 'priority';
          });
        }
      }
      const selectedOne = updatedShippingMethods?.find((shippingMethod: any) => {
        return shippingMethod?.shippingMethodId === cartData?.selectedShippingMethod;
      });
      if (!selectedOne) {
        handleShippingMethodChange(updatedShippingMethods?.[0]);
        setSelectedShippingMethodId(updatedShippingMethods?.[0]?.shippingMethodId);
        setShippingMethodValue(updatedShippingMethods?.[0]?.shippingMethodId);
      } else {
        setSelectedShippingMethodId(selectedOne?.shippingMethodId);
        setShippingMethodValue(selectedOne?.shippingMethodId);
      }
      setShippingMethodsList(updatedShippingMethods);
    }
  };

  useEffect(() => {
    if (fedexResponse) {
      const { modifiedDate, actualArrivalDate, modifiedStandardDate } = getArrivalDateForShippingMethod(
        fedexResponse,
        getShippingMethodDisplayName(shippingMethodsList, selectedShippingMethodId ?? ''),
        projectSettings?.defaultDeliveryDates,
        projectSettings,
        cartData?.containsOnlyDropShipItems ?? cartData?.containsOnlyDropShipAndPersonalizedItems,
        cartData?.containsOnlyPersonalizedItems,
        shippingBackorderDate,
      );
      const currentSelectedShippingMethod = shippingMethodsList?.find(
        (method) => method?.shippingMethodId === selectedShippingMethodId,
      );

      if (currentSelectedShippingMethod) {
        const shippingInfo = {
          shippingMethodId: currentSelectedShippingMethod?.shippingMethodId,
          date: modifiedDate,
          standardDeliveryDate: modifiedStandardDate,
          actualArrivalDate,
          name: currentSelectedShippingMethod?.displayName,
        };
        localStorage.setItem('cartItemsDate', JSON.stringify(shippingInfo));
      }
    }
  }, [fedexResponse, selectedShippingMethod]);

  useEffect(() => {
    if (lineItemsAvailibiltyMsgs?.length > 0) {
      const { modifiedDate, actualArrivalDate, modifiedStandardDate } = getArrivalDateForShippingMethod(
        fedexResponse,
        getShippingMethodDisplayName(shippingMethodsList, selectedShippingMethodId ?? ''),
        projectSettings?.defaultDeliveryDates,
        projectSettings,
        cartData?.containsOnlyDropShipItems ?? cartData?.containsOnlyDropShipAndPersonalizedItems,
        cartData?.containsOnlyPersonalizedItems,
        shippingBackorderDate,
      );
      const currentSelectedShippingMethod = shippingMethodsList?.find(
        (method) => method?.shippingMethodId === selectedShippingMethodId,
      );
      if (currentSelectedShippingMethod) {
        const shippingInfo = {
          shippingMethodId: currentSelectedShippingMethod?.shippingMethodId,
          date: modifiedDate,
          standardDeliveryDate: modifiedStandardDate,
          actualArrivalDate,
          name: currentSelectedShippingMethod?.displayName,
        };
        localStorage.setItem('cartItemsDate', JSON.stringify(shippingInfo));
        setCartDeliveryDate(JSON.stringify(shippingInfo));
      }
    }
  }, [fedexResponse, selectedShippingMethodId, lineItemsAvailibiltyMsgs]);

  useEffect(() => {
    const fetchArrivalDate = async () => {
      if (projectSettings?.isFedexEnabled && shippingZipCode && fedexCallFlag) {
        try {
          const payload: GetEstimatedDeliveryDatePayload = createFedexRequestPayload(shippingZipCode, 'CHECKOUT');
          const response: any = await getEstimatedDeliveryDate(payload);
          if (!response?.isError) {
            setFedexResponse(response);
          }
        } catch (error) {}
      }
    };
    fetchArrivalDate();
  }, [shippingZipCode]);

  useEffect(() => {
    if (lineItemsAvailibiltyMsgs?.length > 0) {
      const fetchArrivalDate = async () => {
        if (!fedexResponse && projectSettings?.isFedexEnabled && shippingZipCode) {
          try {
            const payload: GetEstimatedDeliveryDatePayload = createFedexRequestPayload(shippingZipCode, 'CHECKOUT');
            const response: any = await getEstimatedDeliveryDate(payload);
            setFedexCallFlag(true);
            if (!response?.isError) {
              setFedexResponse(response);
            }
          } catch (error) {
            setFedexCallFlag(true);
          }
        }
      };
      fetchArrivalDate();
    }
  }, [lineItemsAvailibiltyMsgs, shippingZipCode]);

  useEffect(() => {
    if (lineItems) {
      const isExpShippingProductAvailable = isExpShippingAvailable(cartData?.lineItems);
      const isPriorityShippingProductAvailable = isPriorityShippingAvailable(cartData?.lineItems);
      if (!isExpShippingProductAvailable || !isPriorityShippingProductAvailable) {
        filterShippingMethods(isExpShippingProductAvailable, isPriorityShippingProductAvailable);
      } else {
        setShippingMethodsList(shippingMethods);
      }
    }
  }, [lineItems, shippingMethods]);

  const allMethodsData =
    shippingMethodsList?.map((shippingMethod) => {
      const { modifiedDate, actualArrivalDate, modifiedStandardDate } = getArrivalDateForShippingMethod(
        fedexResponse,
        getShippingMethodDisplayName(shippingMethods, shippingMethod.shippingMethodId),
        projectSettings?.defaultDeliveryDates,
        projectSettings,
        cartData?.containsOnlyDropShipItems ?? cartData?.containsOnlyDropShipAndPersonalizedItems,
        cartData?.containsOnlyPersonalizedItems,
        shippingBackorderDate,
      );
      return {
        shippingMethodId: shippingMethod?.shippingMethodId,
        name: shippingMethod?.name,
        displayName: shippingMethod?.displayName,
        rates: shippingMethod?.rates,
        modifiedDate,
        actualArrivalDate,
        modifiedStandardDate,
        isApplicable: shippingMethod?.isApplicable,
      };
    }) ?? [];

  return (
    <>
      <p className="mb-6 text-lg font-bold">{mapLocaleToMeaningfulFormat().shippingMethods}</p>
      {isLoading && <Loader />}
      <RadioGroup value={selectedShippingMethodId}>
        {shippingMethodsList &&
          shippingMethodsList?.length > 0 &&
          allMethodsData?.map((shippingMethod, index) => {
            return (
              <label
                className={`${
                  selectedShippingMethodId === shippingMethod?.shippingMethodId
                    ? 'border-2 border-black'
                    : 'border-silverGray'
                } mb-3 flex cursor-pointer space-x-2 rounded-md border p-4 shadow-md focus:border-black lg:items-center lg:p-4`}
                key={shippingMethod?.shippingMethodId}
                htmlFor={shippingMethod?.shippingMethodId}
              >
                <RadioGroupItem
                  value={shippingMethod?.shippingMethodId}
                  id={shippingMethod?.shippingMethodId}
                  defaultChecked={selectedShippingMethodId == shippingMethod?.shippingMethodId || index === 0}
                  className="mt-1 h-[19px] w-[19px] checked:translate-y-[-50%]"
                  aria-label={shippingMethod?.name}
                  tabIndex={index}
                  onClick={() =>
                    handleShippingMethodChange({
                      shippingMethodId: shippingMethod?.shippingMethodId,
                      date: shippingMethod?.modifiedDate,
                      actualArrivalDate: shippingMethod?.actualArrivalDate,
                      standardDeliveryDate: shippingMethod?.modifiedStandardDate,
                      name: shippingMethod?.displayName,
                      isApplicable: shippingMethod?.isApplicable,
                    })
                  }
                />
                <section className="flex w-full justify-between lg:items-center">
                  {shippingMethod?.modifiedDate && (
                    <section className="ml-2 gap-20 lg:flex">
                      <p className="text-base lg:w-80">
                        {mapLocaleToMeaningfulFormat().arrivesBy}
                        <strong className="ml-1 whitespace-nowrap">
                          {formatArrivalDate(shippingMethod?.modifiedDate, mapLocaleToMeaningfulFormat())}
                        </strong>
                      </p>
                      <p className="">{shippingMethod.displayName}</p>
                    </section>
                  )}
                  {isFreeShippingEligible(shippingMethod?.displayName ?? '') ? (
                    <p className="text-scarletRed">{mapLocaleToMeaningfulFormat().free}</p>
                  ) : (
                    shippingMethod?.rates?.[0]?.price && (
                      <p className="font-bold">
                        {shippingMethod.rates[0].price.centAmount === 0 || isNoChargeOrder ? (
                          <span className="text-scarletRed">{mapLocaleToMeaningfulFormat().free}</span>
                        ) : (
                          CurrencyHelpers.formatForCurrency(shippingMethod.rates?.[0].price as Money)
                        )}
                      </p>
                    )
                  )}
                </section>
              </label>
            );
          })}
      </RadioGroup>
      {showShippingNotApplicableError && selectedShippingMethod?.displayName && (
        <p className="font-medium leading-6 text-scarletRed">
          <strong>{mapLocaleToMeaningfulFormat().shippingMethodNotApplicableError}</strong>
        </p>
      )}
    </>
  );
};

export default ShippingMethodRadio;
